import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Scale, ArrowLeft, Play, CheckCircle2, XCircle, Undo2 } from "lucide-react";
import { Select } from "antd";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { brandMutedText, brandPanelBorder, brandPanelGradient } from "../../components/ui/statusStyles";
import {
    APPRAISAL_CALIBRATION_ADJUSTMENTS, APPRAISAL_CALIBRATION_SESSIONS,
} from "../../constants/api";

const POTENTIAL = [
    { value: 1, label: "Low" },
    { value: 2, label: "Medium" },
    { value: 3, label: "High" },
];
const potentialLabel = (v) => POTENTIAL.find((p) => p.value === v)?.label || "—";

/**
 * The calibration room.
 *
 * Nothing on this screen changes an appraisal until the session is completed,
 * which is what makes it safe to argue in. The "proposed" column is a pending
 * change; the original stays visible beside it the whole time so the room can
 * always see what the manager actually submitted.
 */
const CalibrationSessionDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { fetch, create, update, remove } = useApiMethods();

    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState({ adjusted_score: "", adjusted_potential: null, reason: "" });
    const [confirmComplete, setConfirmComplete] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(APPRAISAL_CALIBRATION_SESSIONS, { id });
            setSession(res?.status === "success" ? res.data : null);
        } catch (_) {
            setSession(null);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const lifecycle = async (action) => {
        setBusy(true);
        try {
            const res = await update(APPRAISAL_CALIBRATION_SESSIONS, { id: Number(id), action });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Done");
                await load();
            } else {
                ToastMsg("error", res?.message || "Could not do that");
            }
        } catch (_) {
            ToastMsg("error", "Could not do that");
        } finally {
            setBusy(false);
            setConfirmComplete(false);
        }
    };

    const saveAdjustment = async (row) => {
        if (!draft.reason.trim()) {
            ToastMsg("error", "Record why the rating is being adjusted");
            return;
        }
        if (draft.adjusted_score === "" && draft.adjusted_potential === null) {
            ToastMsg("error", "Adjust the rating, the potential, or both");
            return;
        }
        setBusy(true);
        try {
            const body = {
                session_id: Number(id),
                appraisal_id: row.appraisal_id,
                reason: draft.reason.trim(),
            };
            // Only send the halves actually filled in: the API preserves the
            // other half of an existing proposal rather than clearing it.
            if (draft.adjusted_score !== "") body.adjusted_score = Number(draft.adjusted_score);
            if (draft.adjusted_potential !== null) body.adjusted_potential = draft.adjusted_potential;

            const res = await create(APPRAISAL_CALIBRATION_ADJUSTMENTS, body);
            if (res?.status === "success") {
                setEditing(null);
                setDraft({ adjusted_score: "", adjusted_potential: null, reason: "" });
                await load();
            } else {
                ToastMsg("error", res?.message || "Could not record the adjustment");
            }
        } catch (_) {
            ToastMsg("error", "Could not record the adjustment");
        } finally {
            setBusy(false);
        }
    };

    const withdraw = async (adjustmentId) => {
        setBusy(true);
        try {
            const res = await remove(`${APPRAISAL_CALIBRATION_ADJUSTMENTS}?id=${adjustmentId}`);
            if (res?.status === "success") await load();
            else ToastMsg("error", res?.message || "Could not withdraw");
        } catch (_) {
            ToastMsg("error", "Could not withdraw");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return <ContainerCard scrollable>
            <div className="text-center py-12 text-muted-foreground">Loading…</div>
        </ContainerCard>;
    }
    if (!session) {
        return <ContainerCard scrollable>
            <div className="text-center py-12 text-muted-foreground">Session not found.</div>
        </ContainerCard>;
    }

    const roster = session.roster || [];
    const dist = session.distribution || { before: {}, after: {}, total: 0 };
    const open = session.status === "in_progress";
    const proposals = roster.filter((r) => r.adjustment && !r.adjustment.applied_at).length;
    const allBands = Array.from(new Set([...Object.keys(dist.before), ...Object.keys(dist.after)]));

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard Icon={Scale} title={session.name}
                subtitle={`${session.cycle_name} · ${session.department_name}`} />

            <div className="px-3 pb-6 space-y-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/appraisal/calibration")}>
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700/40">
                            {String(session.status).replace("_", " ")}
                        </span>
                        {session.status === "draft" && (
                            <Button size="sm" variant="brand" disabled={busy} onClick={() => lifecycle("start")}>
                                <Play className="w-4 h-4 mr-1" /> Start session
                            </Button>
                        )}
                        {open && (
                            <>
                                <Button size="sm" variant="brand" disabled={busy}
                                    onClick={() => setConfirmComplete(true)}>
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> Complete &amp; apply
                                </Button>
                                <Button size="sm" variant="outline" disabled={busy}
                                    onClick={() => lifecycle("cancel")}>
                                    <XCircle className="w-4 h-4 mr-1" /> Cancel
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                {session.status === "draft" && (
                    <div className={cn("rounded-md border p-3 text-sm", brandPanelBorder, brandPanelGradient, brandMutedText)}>
                        Start the session to begin proposing adjustments. Nothing changes an
                        appraisal until the session is completed.
                    </div>
                )}
                {session.status === "completed" && (
                    <div className="rounded-md border border-green-200 dark:border-green-900/40 bg-green-50 dark:bg-green-900/20 p-3 text-sm">
                        Completed {session.completed_at && dayjs(session.completed_at).format("DD-MM-YYYY")}.
                        The adjustments below have been applied and recorded on each appraisal's history.
                    </div>
                )}

                {/* Distribution is the first thing a room needs: the usual reason
                    to calibrate is that one manager's curve looks nothing like
                    another's. */}
                {allBands.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-border p-4">
                        <h3 className="text-sm font-semibold mb-2">
                            Rating spread{proposals > 0 ? ` · ${proposals} proposed change(s)` : ""}
                        </h3>
                        <div className="space-y-1">
                            {allBands.map((band) => (
                                <div key={band} className="flex items-center gap-2 text-xs">
                                    <span className="w-44 truncate">{band}</span>
                                    <span className="text-muted-foreground w-24">
                                        {dist.before[band] || 0} now
                                    </span>
                                    <span className={cn("w-28",
                                        (dist.after[band] || 0) !== (dist.before[band] || 0)
                                            ? "text-blue-600 dark:text-blue-400 font-medium" : "text-muted-foreground")}>
                                        → {dist.after[band] || 0} after
                                    </span>
                                </div>
                            ))}
                            <p className="text-xs text-muted-foreground pt-1">{dist.total} in this cohort</p>
                        </div>
                    </div>
                )}

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-border p-4">
                    <h3 className="text-sm font-semibold mb-3">Cohort</h3>
                    {roster.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                            Nobody is in scope yet. Appraisals join this session once HR approves them.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {roster.map((r) => {
                                const adj = r.adjustment;
                                return (
                                    <div key={r.appraisal_id} className="border border-border rounded-lg p-3">
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <div className="min-w-[180px]">
                                                <div className="font-medium text-sm">{r.employee?.name}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {r.employee?.designation || "—"}
                                                    {r.reporting_manager?.name && ` · RM ${r.reporting_manager.name}`}
                                                </div>
                                            </div>
                                            <div className="text-xs">
                                                <div>Score <span className="font-medium">{r.final_score ?? "—"}</span>
                                                    {r.final_rating ? ` · ${r.final_rating}` : ""}</div>
                                                <div className="text-muted-foreground">
                                                    Potential {potentialLabel(r.potential_rating)}
                                                </div>
                                            </div>
                                            {adj && (
                                                <div className="text-xs rounded-md bg-blue-50 dark:bg-blue-900/20 px-2 py-1">
                                                    <div className="font-medium text-blue-800 dark:text-blue-300">
                                                        {adj.applied_at ? "Applied" : "Proposed"}:{" "}
                                                        {adj.adjusted_score ?? "—"}
                                                        {adj.adjusted_rating ? ` (${adj.adjusted_rating})` : ""}
                                                        {adj.adjusted_potential
                                                            ? ` · potential ${potentialLabel(adj.adjusted_potential)}` : ""}
                                                    </div>
                                                    <div className="text-muted-foreground">
                                                        was {adj.original_score ?? "—"}
                                                        {adj.original_potential
                                                            ? ` · ${potentialLabel(adj.original_potential)}` : ""}
                                                        {" — "}{adj.reason}
                                                    </div>
                                                </div>
                                            )}
                                            {open && (
                                                <div className="flex gap-1">
                                                    <Button size="sm" variant="outline"
                                                        onClick={() => {
                                                            setEditing(editing === r.appraisal_id ? null : r.appraisal_id);
                                                            setDraft({
                                                                adjusted_score: adj?.adjusted_score ?? "",
                                                                adjusted_potential: adj?.adjusted_potential ?? null,
                                                                reason: adj?.reason || "",
                                                            });
                                                        }}>
                                                        {adj ? "Revise" : "Adjust"}
                                                    </Button>
                                                    {adj && !adj.applied_at && (
                                                        <Button size="sm" variant="ghost" disabled={busy}
                                                            onClick={() => withdraw(adj.id)}>
                                                            <Undo2 className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {open && editing === r.appraisal_id && (
                                            <div className="mt-3 space-y-2 border-t border-border pt-3">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1">
                                                            Adjusted score (leave blank to keep)
                                                        </label>
                                                        <Input type="number" step="0.1" min={0}
                                                            value={draft.adjusted_score}
                                                            onChange={(e) => setDraft({ ...draft, adjusted_score: e.target.value })} />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1">
                                                            Adjusted potential
                                                        </label>
                                                        <Select allowClear className="w-full"
                                                            placeholder="Keep as is"
                                                            value={draft.adjusted_potential}
                                                            onChange={(v) => setDraft({ ...draft, adjusted_potential: v ?? null })}
                                                            options={POTENTIAL} />
                                                    </div>
                                                </div>
                                                <Textarea rows={2}
                                                    placeholder="Why is this being adjusted? Recorded on the appraisal's history."
                                                    value={draft.reason}
                                                    onChange={(e) => setDraft({ ...draft, reason: e.target.value })} />
                                                <div className="flex gap-2">
                                                    <Button size="sm" variant="brand" disabled={busy}
                                                        onClick={() => saveAdjustment(r)}>Record</Button>
                                                    <Button size="sm" variant="ghost" disabled={busy}
                                                        onClick={() => setEditing(null)}>Cancel</Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <ConfirmPopupModal
                open={confirmComplete}
                onClose={() => setConfirmComplete(false)}
                onConfirm={() => lifecycle("complete")}
                title="Complete calibration"
                description={`This applies ${proposals} proposed change(s) to the appraisals and writes each one to its history. Completed sessions cannot be edited or reopened.`}
                type="warning"
                confirmLabel="Complete and apply"
            />
        </ContainerCard>
    );
};

export default CalibrationSessionDetail;
